import {
  useAsyncData,
  useRequestURL,
  useRoute,
  useRuntimeConfig,
} from '#imports'
import { ref } from 'vue'

import type { ApiBlock } from '@backmarket/http-api/src/api-specs-content/models/block'
import {
  type CMSPageSectionName,
  type CMSSectionsAPI,
  sectionBlockKeys,
} from '@backmarket/http-api/src/api-specs-content/models/cms-sections'
import type { HttpError } from '@backmarket/http-api/src/utils/HttpApiError'
import { blockValidationErrors } from '@backmarket/nuxt-layer-cms/validateBlock'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import type { Owner } from '@backmarket/nuxt-module-logger/types'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import { formatBlock } from '../helpers/formatBlock'

import { useAddSectionsCacheTagHeader } from './useSetCMSPageCacheTagHeader'

type CMSSectionsFetchOptions = {
  timeout?: number
}

type BaseParams = {
  pageName: CMSPageSectionName
  slug: string | undefined
  fetchOptions?: CMSSectionsFetchOptions
}

type ParamsWithPublicId = BaseParams & {
  publicId: string | undefined
  categoryId?: never
}

type ParamsWithCategoryId = BaseParams & {
  categoryId: string | undefined
  publicId?: never
}

/**
 * Either `publicId` (calls `/content/page-sections/:pageName/:slug/:publicId`)
 * or `categoryId` (calls `/content/page-sections/:pageName/:slug?categoryId=`).
 */
export type UseFetchFormattedCMSSectionsParams =
  | ParamsWithPublicId
  | ParamsWithCategoryId

type CMSSectionsRequest = { publicId: string } | { categoryId: string }

const DEFAULT_OWNERS: Owner[] = ['bot-squad-content-front']

function getEmptyCMSSections(): CMSSectionsAPI {
  return {
    id: '',
    technicalName: '',
    section1: [],
    section2: [],
    section3: [],
    section4: [],
    section5: [],
  }
}

function getEmptyResult() {
  return {
    data: ref(getEmptyCMSSections()),
    error: ref(null),
  }
}

function getCMSSectionsRequest({
  publicId,
  categoryId,
}: Pick<UseFetchFormattedCMSSectionsParams, 'publicId' | 'categoryId'>):
  | CMSSectionsRequest
  | undefined {
  if (publicId) return { publicId }
  if (categoryId) return { categoryId }

  return undefined
}

function formatBlocks(blocks: ApiBlock[], currentUrl: URL): ApiBlock[] {
  return blocks
    .filter((block) => blockValidationErrors(block).length === 0)
    .map((block) => formatBlock(block, currentUrl))
}

function formatCMSSections(
  cmsSections: CMSSectionsAPI,
  currentUrl: URL,
): CMSSectionsAPI {
  const formattedSections: CMSSectionsAPI = {
    ...getEmptyCMSSections(),
    id: cmsSections.id,
    technicalName: cmsSections.technicalName,
  }

  sectionBlockKeys.forEach((key) => {
    formattedSections[key] = formatBlocks(cmsSections[key] ?? [], currentUrl)
  })

  return formattedSections
}

/**
 * Fetches and formats the CMS page sections of a page from the Content API.
 * It also adds the sections Cache-Tag header (server-side), so consumers don't
 * have to.
 *
 * @example
 * // With a public id (e.g. Product page)
 * const { data } = useFetchFormattedCMSSections({
 *   pageName: CMS_PAGE_SECTION_NAMES.PRODUCT,
 *   slug,
 *   publicId: productId,
 * })
 *
 * @example
 * // With a category id (e.g. Trade-in product page, `trade_in_product` route)
 * const { slug, category } = useRouteParams()
 * const { data } = useFetchFormattedCMSSections({
 *   pageName: CMS_PAGE_SECTION_NAMES.BUYBACK_MODEL_PAGE,
 *   slug,
 *   categoryId: category,
 * })
 */
export function useFetchFormattedCMSSections({
  pageName,
  slug,
  publicId,
  categoryId,
  fetchOptions,
}: UseFetchFormattedCMSSectionsParams) {
  const { CMS_PAGE_TIMEOUT, KILL_CMS_SECTIONS_PAGES = '' } =
    useRuntimeConfig().public
  const route = useRoute()
  const logger = useLogger()

  const { timeout = Number(CMS_PAGE_TIMEOUT), ...remainingFetchOptions } =
    fetchOptions ?? {}

  const owners = route.meta?.owners?.length ? route.meta.owners : DEFAULT_OWNERS

  const killedPages = KILL_CMS_SECTIONS_PAGES.split(',')
    .map((page) => page.trim())
    .filter(Boolean)
  const routeName = route.name?.toString()

  if (routeName && killedPages.includes(routeName)) {
    return getEmptyResult()
  }

  const request = getCMSSectionsRequest({ publicId, categoryId })

  if (!slug || !request) {
    logger.error('[CMS:useFetchFormattedComponentBlock] missing params', {
      error_context: { pageName, slug, publicId, categoryId },
      owners,
    })

    return getEmptyResult()
  }

  const $httpFetch = useHttpFetch()
  const currentUrl = useRequestURL()

  const fetchCMSSections = async () => {
    try {
      if ('publicId' in request) {
        return await $httpFetch(
          '/content/page-sections/:pageName/:slug/:publicId',
          {
            ...remainingFetchOptions,
            timeout,
            pathParams: { pageName, slug, publicId: request.publicId },
          },
        )
      }

      return await $httpFetch('/content/page-sections/:pageName/:slug', {
        ...remainingFetchOptions,
        timeout,
        pathParams: { pageName, slug },
        query: { categoryId: request.categoryId },
      })
    } catch (error) {
      logger.error('[useFetchFormattedCMSSections] Failed to fetch Sections', {
        error: error as HttpError,
        owners,
      })

      throw error
    }
  }

  const asyncData = useAsyncData<CMSSectionsAPI>(
    getAsyncDataKey('cms-sections', { pageName, slug, publicId, categoryId }),
    fetchCMSSections,
    {
      transform: (cmsSections) => formatCMSSections(cmsSections, currentUrl),
      dedupe: 'defer',
    },
  )

  // Purges the page from the Cloudflare cache when its sections are updated.
  // Getter: the data is not resolved yet at this synchronous point.
  useAddSectionsCacheTagHeader(() => asyncData.data.value?.id)

  return asyncData
}
